import { useEffect, useState, type ComponentProps, type ReactNode } from "react";
import { ProductOnboarding } from "./product-onboarding.js";
import { MarketingLanding } from "./MarketingLanding.js";
import "./product-entry.css";
import { publicMarketingRequested } from "./public-marketing-route.js";

type EntryScreen = "LANDING" | "SIGN_IN" | "SIGN_UP";
type EntryProps = Omit<ComponentProps<typeof ProductOnboarding>, "initialAuthMode">;

export function publicEntryScreen(hash: string): EntryScreen {
  if (hash === "#create-account") return "SIGN_UP";
  // Section links stay on the public page; existing app deep links need sign-in.
  if (hash.startsWith("#landing-")) return "LANDING";
  return hash && hash !== "#" ? "SIGN_IN" : "LANDING";
}

// WHY: a marketing action must open the existing account form in the promised
// mode. This wrapper owns only signed-out entry, never the authenticated app,
// its mounted Voice, or its sibling confirmation dialog. Confirmation and
// business-switch entry outrank the marketing page and retain their own logic.
export function ProductEntry(props: EntryProps) {
  const [screen, setScreen] = useState<EntryScreen>(() =>
    publicEntryScreen(typeof window === "undefined" ? "" : window.location.hash));
  useEffect(() => {
    const restore = () => {
      const next = publicEntryScreen(window.location.hash);
      setScreen(next);
      // Marketing sections can be far down the page. Direct auth links must
      // enter at the form heading, not inherit a pricing-section scroll offset.
      if (next !== "LANDING") window.scrollTo(0, 0);
    };
    window.addEventListener("hashchange", restore);
    return () => window.removeEventListener("hashchange", restore);
  }, []);
  const open = (next: "SIGN_IN" | "SIGN_UP") => {
    window.location.hash = next === "SIGN_UP" ? "create-account" : "sign-in";
    setScreen(next);
    window.scrollTo(0, 0);
  };
  const directEntry = props.confirmationLinkToken !== undefined || Boolean(props.initialBusinessSlug);
  if (!directEntry && screen === "LANDING") {
    return <MarketingLanding onGetStarted={() => open("SIGN_UP")} onSignIn={() => open("SIGN_IN")} />;
  }
  return <div className="product-entry">
    {!directEntry ? <a className="product-entry__home" href="#">← Business Ops</a> : null}
    <ProductOnboarding key={directEntry ? "direct" : screen} {...props}
      initialAuthMode={screen === "SIGN_UP" && !directEntry ? "SIGN_UP" : "SIGN_IN"} />
  </div>;
}

// WHY: a saved login previously replaced every SEO page with the private desk.
// Own only explicit public routes. Ordinary app hashes and confirmation stay
// with the existing workspace owner; unchanged false state never remounts it.
export function PublicMarketingRoute({ children, forceApp }: { children: ReactNode; forceApp: boolean }) {
  const current = () => typeof window !== "undefined" && publicMarketingRequested(window.location.pathname, window.location.hash);
  const [showMarketing, setShowMarketing] = useState(current);
  useEffect(() => {
    const update = () => setShowMarketing(current());
    window.addEventListener("hashchange", update);
    window.addEventListener("popstate", update);
    return () => { window.removeEventListener("hashchange", update); window.removeEventListener("popstate", update); };
  }, []);
  if (!forceApp && showMarketing) return <MarketingLanding
    onGetStarted={() => { window.location.href = "/#create-account"; }}
    onSignIn={() => { window.location.href = "/#sign-in"; }}
  />;
  return children;
}
